import { useEffect, useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "motion/react";
import { PROFILE } from "../../config/profile";
import { playShutdownChime } from "../../utils/sound";
import windowsIcon from "../../assets/images/windows-0.png";

type Stage = "saving" | "powerOff" | "safe";

interface ShutdownScreenProps {
  onRestart: () => void;
}

const ShutdownScreen = ({ onRestart }: ShutdownScreenProps) => {
  const [stage, setStage] = useState<Stage>("saving");

  useEffect(() => {
    playShutdownChime();
    const toPowerOff = setTimeout(() => setStage("powerOff"), 1800);
    const toSafe = setTimeout(() => setStage("safe"), 2700);
    return () => {
      clearTimeout(toPowerOff);
      clearTimeout(toSafe);
    };
  }, []);

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={() => stage === "safe" && onRestart()}
      $clickable={stage === "safe"}
    >
      <AnimatePresence>
        {stage !== "safe" && (
          <Saving
            key="saving"
            // CRT power-off: squash to a line, then to a dot
            animate={
              stage === "powerOff"
                ? {
                    scaleY: [1, 0.004, 0.004],
                    scaleX: [1, 1, 0],
                    filter: [
                      "brightness(1)",
                      "brightness(6)",
                      "brightness(10)",
                    ],
                  }
                : {}
            }
            transition={{ duration: 0.7, times: [0, 0.55, 1], ease: "easeIn" }}
          >
            <img src={windowsIcon} alt="" />
            <div>{PROFILE.osName} is shutting down...</div>
          </Saving>
        )}
      </AnimatePresence>
      {stage === "safe" && (
        <Safe
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <p>It's now safe to turn off</p>
          <p>your computer.</p>
          <Hint>Click anywhere to restart</Hint>
        </Safe>
      )}
    </Overlay>
  );
};

export default ShutdownScreen;

const Overlay = styled(motion.div)<{ $clickable: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 200000;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(p) => (p.$clickable ? "pointer" : "wait")};
  overflow: hidden;
`;

const Saving = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  color: #fff;
  font-size: 22px;
  background: radial-gradient(ellipse at center, #3c6ee0 0%, #00309c 80%);

  & img {
    width: 72px;
    height: 72px;
  }
`;

const Safe = styled(motion.div)`
  text-align: center;
  font-family: "VT323", "Courier New", monospace;
  color: #ff8a00;
  font-size: clamp(28px, 5vw, 56px);
  line-height: 1.1;
  text-shadow: 0 0 12px rgba(255, 138, 0, 0.6);
  padding: 0 16px;

  & * {
    font-family: inherit;
  }
`;

const Hint = styled.div`
  margin-top: 32px;
  font-size: 0.4em;
  color: #888;
  text-shadow: none;
`;
